import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "../lib/api";
import Header from "../components/app/Header";
import { toast } from "sonner";
import ContactDetailPanel from "../components/app/ContactDetailPanel";
import ReactivationDialog from "../components/app/ReactivationDialog";
import BusinessProfileCard from "../components/app/BusinessProfileCard";
import CampaignsManager from "../components/app/CampaignsManager";
import AiSensyUserPanel from "../components/app/AiSensyUserPanel";
import SendCampaignDialog from "../components/app/SendCampaignDialog";
import RepliesInbox from "../components/app/RepliesInbox";
import AutopilotProposals from "../components/app/AutopilotProposals";
import {
  Radar,
  RefreshCw,
  Zap,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Filter,
  Clock,
  Snowflake,
  History,
  Ban,
  Inbox,
  Sparkles,
  Users,
  MessageSquare,
} from "lucide-react";

const BAND_STYLES = {
  hot: "bg-rose-500/10 border-rose-500/30 text-rose-300",
  warm: "bg-amber-500/10 border-amber-500/30 text-amber-300",
  cool: "bg-sky-500/10 border-sky-500/30 text-sky-300",
  cold: "bg-zinc-800/40 border-zinc-700 text-zinc-400",
};

function fmtSyncedAgo(iso) {
  if (!iso) return "never";
  try {
    const diff = Date.now() - new Date(iso).getTime();
    if (diff < 60000) return "just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return `${Math.floor(diff / 86400000)}d ago`;
  } catch {
    return "recently";
  }
}

const FILTERS = ["all", "hot", "warm", "cool", "cold", "unscored"];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
    });
  } catch {
    return "—";
  }
}

function daysSince(iso) {
  if (!iso) return null;
  try {
    const then = new Date(iso).getTime();
    if (!then) return null;
    return Math.floor((Date.now() - then) / 86400000);
  } catch {
    return null;
  }
}

function QuietSetup({ isAdmin }) {
  return (
    <div
      data-testid="ghl-quiet-setup"
      className="rounded-2xl border border-zinc-900 bg-zinc-950/60 p-8 text-center"
    >
      <div className="w-12 h-12 mx-auto rounded-xl bg-zinc-900 border border-zinc-800 flex items-center justify-center">
        <Radar className="w-5 h-5 text-zinc-500" />
      </div>
      <div className="mt-4 text-sm text-zinc-400 leading-relaxed max-w-md mx-auto">
        Your GoHighLevel connection is being set up by your programme team.
      </div>
      {isAdmin && (
        <a
          href="/admin/users"
          data-testid="ghl-admin-shortcut"
          className="mt-4 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-md border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 transition-colors"
        >
          Configure a member&apos;s CRM connection →
        </a>
      )}
    </div>
  );
}

export default function LeadScoring() {
  const [conn, setConn] = useState({ status: "loading" });
  const [meRole, setMeRole] = useState("");
  const [table, setTable] = useState({ contacts: [], stats: null, last_synced_at: null });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncJob, setSyncJob] = useState(null);
  const [scoringJob, setScoringJob] = useState(null);
  const [sort, setSort] = useState("score-desc");
  const [filter, setFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState([]);
  const [openContact, setOpenContact] = useState(null); // full row object
  const [showReactivation, setShowReactivation] = useState(false);
  const [showSendCampaign, setShowSendCampaign] = useState(false);
  const [sendCampaignInitial, setSendCampaignInitial] = useState(null);
  const [pastBatches, setPastBatches] = useState([]);
  const [activeTab, setActiveTab] = useState("leads");
  const [unreadReplies, setUnreadReplies] = useState(0);

  const refreshBatches = useCallback(async () => {
    try {
      const r = await api.get("/reactivation/batches");
      setPastBatches(r.data || []);
    } catch (e) {
      // silent
    }
  }, []);

  const refreshUnread = useCallback(async () => {
    try {
      const r = await api.get("/replies/unread-count");
      setUnreadReplies(r.data?.unread || 0);
    } catch (e) { /* silent */ }
  }, []);

  const refreshTable = useCallback(async () => {
    try {
      const r = await api.get("/employz/contacts-table");
      setTable(r.data || { contacts: [], stats: null });
    } catch (e) {
      console.error(e);
    }
  }, []);

  // Live poll the table while any sync or scoring is in-flight. This is how
  // the progressive-fill UX works — rows update in place as scores land.
  useEffect(() => {
    const queuedNow = table?.stats?.queued_now || 0;
    const syncing_ = syncing || (syncJob && !["done", "error"].includes(syncJob.status));
    if (!queuedNow && !syncing_) return;
    const t = setInterval(refreshTable, 2500);
    return () => clearInterval(t);
  }, [table?.stats?.queued_now, syncing, syncJob, refreshTable]);

  useEffect(() => {
    Promise.all([
      api.get("/employz/connection").then((r) => setConn(r.data || { status: "not_connected" })).catch(() => setConn({ status: "not_connected" })),
      api.get("/auth/me").then((r) => setMeRole(r.data?.role || "")).catch(() => {}),
      refreshTable(),
      refreshBatches(),
      refreshUnread(),
    ]).finally(() => setLoading(false));
  }, [refreshTable, refreshBatches, refreshUnread]);

  // Poll unread reply count every 45s
  useEffect(() => {
    const t = setInterval(refreshUnread, 45000);
    return () => clearInterval(t);
  }, [refreshUnread]);

  // Auto-poll past batches while any is in-flight
  useEffect(() => {
    const anyRunning = (pastBatches || []).some((b) =>
      b.status === "queued" || b.status === "running",
    );
    if (!anyRunning) return;
    const t = setInterval(refreshBatches, 3000);
    return () => clearInterval(t);
  }, [pastBatches, refreshBatches]);

  // Poll sync job progress
  useEffect(() => {
    if (!syncJob?.id || syncJob.status === "done" || syncJob.status === "error") return;
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/employz/sync-status/${syncJob.id}`);
        setSyncJob(r.data);
        if (r.data.status === "done") {
          setSyncing(false);
          const synced = r.data.results?.[0]?.synced || r.data.done;
          const auto = r.data.auto_score;
          if (auto?.queued) {
            toast.success(
              `Synced ${synced} contacts. Auto-scoring ${auto.queued}${auto.overflow ? ` (${auto.overflow} more on demand)` : ""}…`,
              { duration: 4500 }
            );
          } else {
            toast.success(`Synced ${synced} contacts. Nothing needs re-scoring.`);
          }
          refreshTable();
        } else if (r.data.status === "error") {
          setSyncing(false);
          toast.error(r.data.error || "Sync failed.");
        }
      } catch (e) {
        console.error(e);
      }
    }, 1500);
    return () => clearInterval(t);
  }, [syncJob, refreshTable]);

  // Poll scoring job progress and refresh table so live status updates
  useEffect(() => {
    if (!scoringJob?.id || scoringJob.status === "done" || scoringJob.status === "error") return;
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/leads/score/${scoringJob.id}`);
        setScoringJob(r.data);
        refreshTable();
        if (r.data.status === "done" || r.data.status === "error") {
          if (r.data.status === "done") {
            const okCount = (r.data.results || []).filter((x) => !x.error).length;
            toast.success(`${okCount} scored and written to the CRM.`);
          } else {
            toast.error(r.data.error || "Scoring failed.");
          }
        }
      } catch (e) {
        console.error(e);
      }
    }, 2000);
    return () => clearInterval(t);
  }, [scoringJob, refreshTable]);

  const startSync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/employz/sync-contacts");
      setSyncJob({ id: r.data.job_id, status: "queued", done: 0, total: 0 });
    } catch (e) {
      setSyncing(false);
      toast.error(e?.response?.data?.detail || "Could not start sync.");
    }
  };

  const scoreIds = async (ids, label) => {
    if (!ids?.length) {
      toast.error("Nothing to score.");
      return;
    }
    try {
      const r = await api.post("/leads/score-queue", { contact_ids: ids });
      setScoringJob({ id: r.data.job_id, status: "queued", done: 0, total: ids.length, results: [] });
      toast.info(`${label}: ${r.data.queued} queued.`);
      setSelectedIds([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not queue.");
    }
  };

  const scoreAuto = null;  // manual auto-mode button removed — auto-score now fires after every sync

  const filteredSorted = useMemo(() => {
    let rows = [...(table.contacts || [])];
    if (filter !== "all") {
      if (filter === "unscored") rows = rows.filter((r) => r.score_status !== "scored");
      else rows = rows.filter((r) => r.band === filter);
    }
    const bySort = {
      "score-desc": (a, b) => (b.score ?? -1) - (a.score ?? -1),
      "last-activity": (a, b) => (b.last_activity_at || "").localeCompare(a.last_activity_at || ""),
      "date-added": (a, b) => (b.date_added || "").localeCompare(a.date_added || ""),
    }[sort];
    if (bySort) rows.sort(bySort);
    // Unscored always at bottom when sorting by score desc
    if (sort === "score-desc") {
      const scored = rows.filter((r) => r.score != null);
      const unscored = rows.filter((r) => r.score == null);
      rows = [...scored, ...unscored];
    }
    return rows;
  }, [table, filter, sort]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleSelectAll = () => {
    if (selectedIds.length > 0) setSelectedIds([]);
    else setSelectedIds(filteredSorted.map((r) => r.ghl_contact_id));
  };

  const stats = table.stats || {};

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="max-w-7xl mx-auto px-6 md:px-10 pt-8 pb-16 text-sm text-zinc-500">
          Loading…
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-7xl mx-auto px-6 md:px-10 pt-8 pb-16">
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.28em] text-emerald-400">
            // Employz Intelligence / Smart Lead Scoring
          </div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl tracking-tighter font-black text-white">
            Smart Lead Scoring
          </h1>
          <p className="mt-3 text-zinc-400 max-w-2xl text-sm md:text-base leading-relaxed">
            Reads every conversation. Scores what your CRM can&apos;t.
          </p>
        </div>

        {conn.status !== "connected" ? (
          <QuietSetup isAdmin={meRole === "admin" || meRole === "superadmin"} />
        ) : (
          <>
            {/* Tabs — Leads (default) | Replies | Campaigns | Autopilot | Profile */}
            <div className="flex items-center gap-1 mb-6 border-b border-zinc-900 overflow-x-auto" data-testid="ghl-tabs">
              {[
                { k: "leads", label: "Leads", icon: Users },
                { k: "replies", label: "Replies", icon: Inbox, badge: unreadReplies },
                { k: "campaigns", label: "Campaigns", icon: MessageSquare },
                { k: "autopilot", label: "Autopilot", icon: Sparkles },
                { k: "profile", label: "Profile", icon: Radar },
              ].map((t) => {
                const Ico = t.icon;
                return (
                  <button
                    key={t.k}
                    data-testid={`tab-${t.k}`}
                    onClick={() => setActiveTab(t.k)}
                    className={`inline-flex items-center gap-2 px-4 py-3 text-[11px] uppercase tracking-[0.2em] border-b-2 transition-colors relative ${
                      activeTab === t.k
                        ? "border-emerald-500 text-emerald-300"
                        : "border-transparent text-zinc-500 hover:text-zinc-300"
                    }`}
                  >
                    <Ico className="w-3.5 h-3.5" />
                    {t.label}
                    {t.badge > 0 && (
                      <span className="ml-1 bg-emerald-500 text-emerald-950 text-[9px] font-bold rounded-full px-1.5 py-0.5" data-testid={`tab-${t.k}-badge`}>
                        {t.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {activeTab === "replies" && (
              <RepliesInbox onOpenContact={(cid) => {
                const c = (table.contacts || []).find((x) => x.ghl_contact_id === cid);
                if (c) setOpenContact(c);
              }} />
            )}
            {activeTab === "campaigns" && (
              <div className="space-y-6">
                <AiSensyUserPanel />
                <CampaignsManager />
              </div>
            )}
            {activeTab === "autopilot" && (
              <AutopilotProposals onApprove={(p) => {
                setSendCampaignInitial({
                  campaign_id: p.campaign_id,
                  segment: p.segment,
                  context: p.context,
                  guidance: p.guidance,
                  autopilot_proposal_id: p.id,
                });
                setShowSendCampaign(true);
              }} />
            )}
            {activeTab === "profile" && <BusinessProfileCard />}

            {activeTab === "leads" && (
              <>
            {/* Status strip — always visible, live counters */}
            <div className="rounded-2xl border border-zinc-900 bg-zinc-950/60 p-4 flex flex-wrap items-center gap-4 justify-between" data-testid="status-strip">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
                <span className="font-display font-black text-2xl text-white" data-testid="stat-total">{stats.total ?? 0}</span>
                <span className="text-zinc-500">contacts</span>
                <span className="text-zinc-700 mx-1">·</span>
                <span className="font-display font-black text-xl text-emerald-300" data-testid="stat-scored">{stats.scored ?? 0}</span>
                <span className="text-zinc-500">scored</span>
                {(stats.queued_now || 0) > 0 && (
                  <>
                    <span className="text-zinc-700 mx-1">·</span>
                    <span className="font-display font-black text-xl text-amber-300 inline-flex items-center gap-1.5" data-testid="stat-queued-now">
                      <Loader2 className="w-4 h-4 animate-spin" /> {stats.queued_now}
                    </span>
                    <span className="text-zinc-500">scoring now…</span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="send-campaign-btn"
                  onClick={() => { setSendCampaignInitial(null); setShowSendCampaign(true); }}
                  className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 transition-colors"
                  title="Send an approved AiSensy campaign to a segment of contacts, personalized per recipient"
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                  Send campaign
                </button>
                <button
                  data-testid="reactivate-cold-btn"
                  onClick={() => { setSendCampaignInitial({ segment: { bands: ["cold"] } }); setShowSendCampaign(true); }}
                  className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-md border border-sky-500/30 text-sky-300 hover:bg-sky-500/10 transition-colors"
                  title="Shortcut: opens Send campaign with band=cold preselected"
                >
                  <Snowflake className="w-3.5 h-3.5" />
                  Reactivate cold leads
                </button>
                <button
                  data-testid="sync-contacts-btn"
                  onClick={startSync}
                  disabled={syncing}
                  className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-md border border-zinc-800 text-zinc-300 hover:border-emerald-500/30 hover:text-emerald-300 transition-colors disabled:opacity-50"
                  title={table.last_synced_at ? `Last synced ${new Date(table.last_synced_at).toLocaleString()}` : "Never synced"}
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
                  {syncing
                    ? "Syncing…"
                    : table.last_synced_at
                      ? `Sync from CRM · last ${fmtSyncedAgo(table.last_synced_at)}`
                      : "Sync from CRM"}
                </button>
              </div>
            </div>

            {table.auto_score_note && (
              <div className="mt-3 text-[12px] text-amber-300/90 bg-amber-500/5 border border-amber-500/20 rounded-lg px-3 py-2" data-testid="auto-score-note">
                {table.auto_score_note}
              </div>
            )}

            {/* Band summary chips (clickable filters) */}
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="band-summary">
              {[
                { k: "hot", label: "Hot", styles: "border-rose-500/30 text-rose-300 bg-rose-500/5" },
                { k: "warm", label: "Warm", styles: "border-amber-500/30 text-amber-300 bg-amber-500/5" },
                { k: "cool", label: "Cool", styles: "border-sky-500/30 text-sky-300 bg-sky-500/5" },
                { k: "cold", label: "Cold", styles: "border-zinc-700 text-zinc-400 bg-zinc-800/20" },
              ].map((b) => (
                <button
                  key={b.k}
                  data-testid={`band-chip-${b.k}`}
                  onClick={() => setFilter(filter === b.k ? "all" : b.k)}
                  className={`rounded-xl border px-4 py-3 flex items-center justify-between transition-colors ${b.styles} ${filter === b.k ? "ring-1 ring-emerald-500/40" : "hover:brightness-110"}`}
                >
                  <span className="text-[10px] uppercase tracking-[0.22em]">{b.label}</span>
                  <span className="font-display font-black text-xl text-white">{stats[b.k] ?? 0}</span>
                </button>
              ))}
            </div>

            {/* In-flight banners */}
            {syncJob && !["done", "error"].includes(syncJob.status) && (
              <div className="mt-4 rounded-xl border border-sky-500/20 bg-sky-950/20 p-3 text-sm text-sky-200/80" data-testid="sync-status">
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {syncJob.stage || "Syncing…"} {syncJob.done > 0 ? `(${syncJob.done})` : ""}
                </span>
              </div>
            )}
            {scoringJob && !["done", "error"].includes(scoringJob.status) && (
              <div className="mt-4 rounded-xl border border-emerald-500/20 bg-emerald-950/20 p-3 text-sm text-emerald-200/80" data-testid="scoring-status">
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {scoringJob.stage || "Scoring…"} — {scoringJob.done}/{scoringJob.total}
                </span>
              </div>
            )}

            {/* Filter chips + sort */}
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                <Filter className="w-3 h-3" /> Filter
              </div>
              {FILTERS.map((f) => (
                <button
                  key={f}
                  data-testid={`filter-${f}`}
                  onClick={() => setFilter(f)}
                  className={`text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full border transition-colors ${
                    filter === f
                      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                      : "border-zinc-800 text-zinc-500 hover:text-zinc-300"
                  }`}
                >
                  {f}{f !== "all" && stats[f] != null ? ` · ${stats[f]}` : ""}
                </button>
              ))}
              <div className="ml-auto text-[11px] text-zinc-500 inline-flex items-center gap-2">
                Sort
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="bg-zinc-950 border border-zinc-800 rounded-md px-2 py-1 text-zinc-300 text-[11px] focus:outline-none focus:border-emerald-500/30"
                  data-testid="sort-select"
                >
                  <option value="score-desc">Score (high → low)</option>
                  <option value="last-activity">Last activity</option>
                  <option value="date-added">Date added</option>
                </select>
              </div>
            </div>

            {/* Multi-select actions */}
            {selectedIds.length > 0 && (
              <div className="mt-3 flex items-center justify-between rounded-lg border border-emerald-500/20 bg-emerald-950/10 px-3 py-2 text-[12px] text-emerald-200/80">
                <div>{selectedIds.length} selected</div>
                <button
                  data-testid="score-selected-btn"
                  onClick={() => scoreIds(selectedIds, "Score selected")}
                  className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-md border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
                >
                  <Zap className="w-3.5 h-3.5" /> Score selected
                </button>
              </div>
            )}

            {/* Table */}
            <div className="mt-4 rounded-2xl border border-zinc-900 bg-zinc-950/60 overflow-hidden">
              <div className="grid grid-cols-[36px_2fr_2fr_1.5fr_1fr_140px_2fr] items-center gap-3 px-4 py-2.5 text-[10px] uppercase tracking-[0.24em] text-zinc-500 border-b border-zinc-900">
                <input
                  type="checkbox"
                  checked={selectedIds.length > 0 && selectedIds.length === filteredSorted.length}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 accent-emerald-500 cursor-pointer"
                  data-testid="select-all-checkbox"
                />
                <div>Name</div>
                <div>Phone / Email</div>
                <div>Tags</div>
                <div>Last activity</div>
                <div>Score</div>
                <div>Why</div>
              </div>

              {filteredSorted.length === 0 ? (
                <div className="p-8 text-center text-sm text-zinc-500" data-testid="table-empty">
                  {(table.contacts || []).length === 0
                    ? "No contacts yet — hit \"Sync from CRM\" to import them."
                    : "No contacts match this filter."}
                </div>
              ) : (
                <div className="divide-y divide-zinc-900 max-h-[65vh] overflow-y-auto">
                  {filteredSorted.map((c) => {
                    const staleDays = daysSince(c.scored_at);
                    const isStale = c.score != null && staleDays != null && staleDays > 14;
                    const isScoring = c.score_status === "queued" || c.score_status === "scoring";
                    return (
                      <div
                        key={c.ghl_contact_id}
                        data-testid={`contact-row-${c.ghl_contact_id}`}
                        className="grid grid-cols-[36px_2fr_2fr_1.5fr_1fr_140px_2fr] items-center gap-3 px-4 py-3 hover:bg-zinc-900/40 cursor-pointer transition-colors"
                        onClick={(e) => {
                          if (e.target.tagName === "INPUT" || e.target.tagName === "BUTTON") return;
                          setOpenContact(c);
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(c.ghl_contact_id)}
                          onChange={() => toggleSelect(c.ghl_contact_id)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 accent-emerald-500 cursor-pointer"
                          data-testid={`row-check-${c.ghl_contact_id}`}
                        />
                        <div className="text-sm text-white truncate">{c.name || "(no name)"}</div>
                        <div className="text-[12px] text-zinc-500 truncate">
                          <div>{c.phone || "—"}</div>
                          <div>{c.email || ""}</div>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {(c.tags || []).slice(0, 3).map((t) => (
                            <span key={t} className="text-[10px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded border border-zinc-800 text-zinc-500">
                              {t}
                            </span>
                          ))}
                        </div>
                        <div className="text-[12px] text-zinc-500">{fmtDate(c.last_activity_at)}</div>
                        <div className="flex items-center gap-2">
                          {c.score != null ? (
                            <>
                              <span className="font-display font-black text-xl text-white">{c.score}</span>
                              <span className={`text-[9px] uppercase tracking-[0.22em] px-1.5 py-0.5 rounded-full border ${BAND_STYLES[c.band] || BAND_STYLES.cool}`}>
                                {c.band}
                              </span>
                              {isStale && (
                                <span className="text-[9px] uppercase tracking-[0.18em] text-zinc-600 inline-flex items-center gap-0.5" title={`Scored ${staleDays}d ago`}>
                                  <Clock className="w-3 h-3" /> stale
                                </span>
                              )}
                            </>
                          ) : isScoring ? (
                            <span className="text-[10px] uppercase tracking-[0.2em] text-emerald-300/70 inline-flex items-center gap-1">
                              <Loader2 className="w-3 h-3 animate-spin" /> {c.score_status}
                            </span>
                          ) : (
                            <>
                              <span className="text-zinc-500">—</span>
                              <button
                                data-testid={`row-score-btn-${c.ghl_contact_id}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  scoreIds([c.ghl_contact_id], "Score");
                                }}
                                className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
                              >
                                Score
                              </button>
                            </>
                          )}
                          {isStale && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                scoreIds([c.ghl_contact_id], "Re-score");
                              }}
                              className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded border border-zinc-800 text-zinc-400 hover:border-emerald-500/30 hover:text-emerald-300"
                              data-testid={`rescore-btn-${c.ghl_contact_id}`}
                            >
                              Re-score
                            </button>
                          )}
                        </div>
                        <div className="text-[12px] text-zinc-400 truncate" title={c.why || ""}>
                          {c.why || (c.score_status === "error" ? <span className="text-rose-400 inline-flex items-center gap-1"><AlertCircle className="w-3 h-3" /> failed</span> : "—")}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Past Reactivation Campaigns */}
            {pastBatches.length > 0 && (
              <div className="mt-10" data-testid="past-campaigns">
                <div className="flex items-center gap-2 mb-3">
                  <History className="w-4 h-4 text-zinc-500" />
                  <h2 className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">
                    Past reactivation campaigns
                  </h2>
                </div>
                <div className="rounded-2xl border border-zinc-900 bg-zinc-950/60 overflow-hidden">
                  <div className="grid grid-cols-[1fr_1fr_100px_80px_80px_80px_100px] gap-3 px-4 py-2.5 text-[10px] uppercase tracking-[0.24em] text-zinc-500 border-b border-zinc-900">
                    <div>When</div>
                    <div>Account</div>
                    <div>Status</div>
                    <div className="text-right">Sent</div>
                    <div className="text-right">Failed</div>
                    <div className="text-right">Opt-out</div>
                    <div className="text-right">Total</div>
                  </div>
                  <div className="divide-y divide-zinc-900 max-h-64 overflow-y-auto">
                    {pastBatches.map((b) => {
                      const isRunning = b.status === "queued" || b.status === "running";
                      return (
                        <div
                          key={b.id}
                          data-testid={`past-batch-${b.id}`}
                          className="grid grid-cols-[1fr_1fr_100px_80px_80px_80px_100px] gap-3 px-4 py-2.5 text-[12px] items-center"
                        >
                          <div className="text-zinc-400 truncate">
                            {b.created_at ? new Date(b.created_at).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                          </div>
                          <div className="text-zinc-300 truncate">
                            {b.aisensy_label || "(unnamed)"}
                          </div>
                          <div>
                            {isRunning ? (
                              <span className="inline-flex items-center gap-1 text-sky-300 text-[10px] uppercase tracking-[0.18em]">
                                <Loader2 className="w-3 h-3 animate-spin" /> {b.status}
                              </span>
                            ) : b.status === "done" ? (
                              <span className="text-emerald-300 text-[10px] uppercase tracking-[0.18em]">done</span>
                            ) : b.status === "cancelled" ? (
                              <span className="text-amber-300 text-[10px] uppercase tracking-[0.18em] inline-flex items-center gap-1"><Ban className="w-3 h-3" />cancelled</span>
                            ) : (
                              <span className="text-rose-300 text-[10px] uppercase tracking-[0.18em]">error</span>
                            )}
                          </div>
                          <div className="text-right text-emerald-300 font-mono">{b.sent_count || 0}</div>
                          <div className="text-right text-rose-300 font-mono">{b.failed_count || 0}</div>
                          <div className="text-right text-amber-300 font-mono">{(b.opted_out || []).length}</div>
                          <div className="text-right text-zinc-500 font-mono">{b.done || 0}/{b.total || 0}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Note about unscored */}
            {(stats.unscored || 0) > 0 && (
              <div className="mt-3 text-[12px] text-zinc-500 flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-zinc-600" />
                Unscored contacts are scored on demand — pick from the table or hit &quot;Score active contacts&quot;.
              </div>
            )}
              </>
            )}
          </>
        )}

        {openContact && (
          <ContactDetailPanel
            contact={openContact}
            connection={conn}
            onClose={() => setOpenContact(null)}
            onScoreUpdated={refreshTable}
          />
        )}

        <ReactivationDialog
          open={showReactivation}
          onClose={() => {
            setShowReactivation(false);
            refreshBatches();
            refreshTable();
          }}
          onBatchStarted={() => refreshBatches()}
        />
        <SendCampaignDialog
          open={showSendCampaign}
          initial={sendCampaignInitial}
          onClose={() => {
            setShowSendCampaign(false);
            setSendCampaignInitial(null);
            refreshBatches();
            refreshTable();
          }}
        />
      </main>
    </div>
  );
}
